<template>
	<div class="form-container" v-if="form">
		<div v-for="(element, index) in form.fields" :key="index" class="fe-el-container" :class="{'fe-el-required' : element.required, 'fe-el-problematic': invalidElements.indexOf(element) !== -1}">

			<div v-if="element.type === 'heading'" class="fe-el-heading">
				<h3>{{ element.value[language] }}</h3>
			</div>

			<div v-if="element.type === 'inputtext'" class="fe-el-inputtext">
				<label>{{ element.label[language] }}</label>
				<input :disabled="formSubmitted" type="text" v-model="element.value" :placeholder="element.placeholder[language]" @change="validateForm(element);"/>
			</div>

			<div v-if="element.type === 'textarea'" class="fe-el-textarea">
				<label>{{ element.label[language] }}</label>
				<textarea :disabled="formSubmitted" type="text" v-model="element.value" :placeholder="element.placeholder[language]" @change="validateForm(element);"></textarea>
			</div>

			<div v-if="element.type === 'select'" class="fe-el-select">
				<label>{{ element.label[language] }}</label>
				<select :disabled="formSubmitted" v-model="element.value" @change="validateForm(element);">
					<option v-if="!element.required"></option>
					<option v-for="(option, oidx) in element.options" :key="oidx">{{ option.value[language] }}</option>
				</select>
			</div>

			<div v-if="element.type === 'file'" class="fe-el-file">
				<label>
					<span class="fe-el-file-icon"><fa-icon icon="file-upload" /></span>
					<span class="fe-el-file-text">{{ element.label[language] }}</span>
					<input :disabled="formSubmitted" hidden type="file" @change="handleFileChange($event, element);" />
				</label>
				<span class="fe-el-file-valid" v-if="element.file">{{element.file.name}}</span>
				<span class="fe-el-file-valid" v-if="!element.file" > &lt;5MB (PDF, MP3, JPG)</span>
			</div>

			<div v-if="element.type === 'checkbox'" class="fe-el-checkbox">
				<label>
					<input :disabled="formSubmitted" type="checkbox" v-model="element.value" @change="validateForm(element);">
					<span>{{ element.label[language] }}</span>
				</label>
			</div>
		</div>

		<div v-if="!formSubmitted" class="fe-el-buttons">
			<button type="button" class="fe-el-button-reset" @click="resetForm()">
				<fa-icon icon="undo-alt" />
			</button>
			<button type="button" class="fe-el-button-submit" @click="submitForm()">
				<fa-icon v-show="formProcessing" icon="circle-notch" spin />
				<fa-icon v-show="!formProcessing" icon="envelope" />
			</button>
		</div>
		<div v-if="formSubmitted" class="fe-el-submitted">
			<fa-icon icon="check" />
			<div v-if="thankYouMessage !== ''" class="thankyou-message">{{ thankYouMessage.value[language] }}</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import contentDataLoader from '@/store/modules/contentData/content-data-loader';
import staticDataLoader from '@/store/modules/staticData/static-data-loader';

export default {
	mixins: [contentDataLoader, staticDataLoader],
	props: {
		fid: {
			type: [String, Number],
			validator(value) {
				return Number.isInteger(Number(value));
			},
		},
	},
	data() {
		return {
			form: null,
			invalidElements: [],
			formSubmitted: false,
			formProcessing: false,
		};
	},
	created() {
		this.resetForm();
	},
	computed: {
		thankYouMessage() {
			let thankyou = this.form.fields.find(f => f.type === 'thankyou');
			if (!thankyou) {
				thankyou = '';
			}
			return thankyou;
		},
	},
	methods: {
		resetForm() {
			this.form = this.getContent('forms').find(e => e.id === Number(this.fid));
			// copy the entire form object prior to manipulation!!!
			this.form = JSON.parse(JSON.stringify(this.form));
			this.form.fields = JSON.parse(this.form.fields);
		},
		validateForm(incomingEl) {
			const problematicFields = [];

			const validateElement = (element) => {
				let fieldHasData = false;

				if (element.type === 'heading') {
					return true;
				}
				if (element.type === 'inputtext') {
					if (element.value && element.value.trim().length >= 3) {
						fieldHasData = true;
					}
				}
				if (element.type === 'textarea') {
					if (element.value && element.value.trim().length >= 3) {
						fieldHasData = true;
					}
				}
				if (element.type === 'select') {
					if (element.value && element.value.trim().length >= 1) {
						fieldHasData = true;
					}
				}
				if (element.type === 'file') {
					if (element.file && typeof element.file === 'object') {
						fieldHasData = true;
					}
				}
				if (element.type === 'checkbox') {
					if (element.value === true) {
						fieldHasData = true;
					}
				}

				if (!fieldHasData && element.required) {
					problematicFields.push(element);
					return false;
				}

				return true;
			};

			if (incomingEl) {
				const valid = validateElement(incomingEl);
				const idx = this.invalidElements.indexOf(incomingEl);
				if (valid) {
					if (idx !== -1) {
						this.invalidElements.splice(idx, 1);
					}
				} else if (idx === -1) {
					this.invalidElements.push(incomingEl);
				}
			} else {
				this.form.fields.forEach(validateElement);
				this.invalidElements = problematicFields;
				if (problematicFields.length > 0) {
					return false;
				}
			}

			return true;
		},
		submitForm() {
			if (this.formSubmitted && this.formProcessing) return false;
			this.formProcessing = true;

			const formValid = this.validateForm();
			if (formValid) {
				axios.post('/api/submit', this.form).then(() => {
					this.formSubmitted = true;
					this.formProcessing = false;
				});
			} else {
				this.formProcessing = false;
			}
			return true;
		},
		handleFileChange(event, element) {
			const input = event.target;

			if (input.files && input.files[0]) {
				const selectedFile = input.files[0];

				const allowedFiletypes = {
					'application/pdf': ['pdf'],
					'image/jpeg': ['jpeg', 'jpg'],
					'image/png': ['png'],
					'audio/mp3': ['mp3'],
				};

				// 5 MB max hardcoded
				if (selectedFile.size <= 5 * 1024 * 1024) {
					const fReader = new FileReader();
					fReader.onload = (e) => {
						const splitResult = e.target.result.split(',');
						const type = splitResult[0].split(';')[0].split(':')[1];
						const encoding = splitResult[0].split(';')[1];
						const data = splitResult[1];
						const { name, size } = selectedFile;
						const fileExt = name.split('.').pop();

						if (allowedFiletypes[type] && allowedFiletypes[type].indexOf(fileExt) !== -1) {
							element.file = {
								type,
								encoding,
								data,
								name,
								size,
							};
							this.validateForm(element);
						}
					};
					fReader.readAsDataURL(selectedFile);
				}
			}
		},

	},
};
</script>

<style src="@/styles/components/FormView/FormView.less" lang="less" scoped></style>
